import { isSageCostCode } from "./sageCodes"

export interface ParsedJobCodeListRow {
  line: number
  code: string
  name: string
}

function splitRow(line: string): { code: string, name: string } {
  const delimited = line.split(/[,\t;]/).map((part) => part.trim()).filter(Boolean)
  if (delimited.length > 1) {
    return { code: delimited[0], name: delimited.slice(1).join(" ") }
  }
  const tokens = line.split(/\s+/).filter(Boolean)
  return { code: tokens[0] || "", name: tokens.slice(1).join(" ") }
}

function looksLikeHeader(code: string): boolean {
  return !isSageCostCode(code) && /^(code|cstcde|cost|job\s*code|cost\s*code)$/i.test(code)
}

/**
 * Split a dropped plain list into code/name rows. Not an Excel parser —
 * one code per line, optional name after a comma, tab, or space.
 */
export function parseJobCodeList(text: string): ParsedJobCodeListRow[] {
  const rows: ParsedJobCodeListRow[] = []
  const lines = text.split(/\r?\n/)
  lines.forEach((line, index) => {
    const trimmed = line.trim()
    if (!trimmed) return
    const { code, name } = splitRow(trimmed)
    if (!code) return
    rows.push({
      line: index + 1,
      code,
      name: name || code,
    })
  })
  if (rows[0] && looksLikeHeader(rows[0].code)) {
    return rows.slice(1)
  }
  return rows
}

export function isPlainJobCodeListFile(file: File): boolean {
  const name = file.name.toLowerCase()
  return name.endsWith(".txt") || name.endsWith(".csv") || name.endsWith(".tsv")
}

export function readDroppedText(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ""))
    reader.onerror = () => reject(reader.error || new Error("Could not read file"))
    reader.readAsText(file)
  })
}
